import { adminApiRequest } from './api';
import { getApiUrl } from '../config/environment';
import {
  isTeamRouteAuthorizationError,
  parseThrownAdminError,
} from '../admin/utils/adminApiError';

// Admin User Management Service
export const adminUserService = {
  // Get all users with pagination and filtering
  getUsers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.role) queryParams.append('role', params.role);
    if (params.status) queryParams.append('status', params.status);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `/admin/users${queryString ? `?${queryString}` : ''}`;
    
    return adminApiRequest(url);
  },

  // Get specific user by ID
  getUser: async (userId) => {
    return adminApiRequest(`/admin/users/${userId}`);
  },

  /**
   * Optional: GET /api/admin/users/:userId/cart — current server cart for this user.
   * Returns { success, data } or { success: false } if route not implemented.
   */
  getUserCartSnapshot: async (userId) => {
    const id = encodeURIComponent(String(userId ?? '').trim());
    if (!id) return { success: false, data: null };
    try {
      return await adminApiRequest(`/admin/users/${id}/cart`);
    } catch {
      return { success: false, data: null };
    }
  },

  /**
   * Optional: GET /api/admin/users/:userId/wishlist — wishlist / liked products for this user.
   */
  getUserWishlistSnapshot: async (userId) => {
    const id = encodeURIComponent(String(userId ?? '').trim());
    if (!id) return { success: false, data: null };
    try {
      return await adminApiRequest(`/admin/users/${id}/wishlist`);
    } catch {
      return { success: false, data: null };
    }
  },

  /**
   * Optional: GET /api/admin/users/:userId/recent-visits — recent product views for this user.
   */
  getUserRecentVisitsSnapshot: async (userId) => {
    const id = encodeURIComponent(String(userId ?? '').trim());
    if (!id) return { success: false, data: null };
    try {
      return await adminApiRequest(`/admin/users/${id}/recent-visits`);
    } catch {
      return { success: false, data: null };
    }
  },

  // Update user information
  // API: PUT /api/admin/users/:userId (Admin only)
  // Body: { name, email, phone, role, isActive, addresses, accessPermissions, isVerified }
  updateUser: async (userId, userData) => {
    return adminApiRequest(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  },

  // Update team member access permissions - DEDICATED ENDPOINT
  // API: PUT /api/admin/users/:userId/access
  // Backend must save accessPermissions to user document
  updateUserAccess: async (userId, accessPermissions) => {
    return adminApiRequest(`/admin/users/${userId}/access`, {
      method: 'PUT',
      body: JSON.stringify({ accessPermissions })
    });
  },

  /**
   * Logged-in admin/team member changes their own password (requires current password).
   * Tries self-service routes — not PUT /admin/users/:id/password (admin-only reset).
   */
  changeOwnPassword: async ({ currentPassword, newPassword }) => {
    const body = JSON.stringify({
      currentPassword: String(currentPassword ?? ''),
      newPassword: String(newPassword ?? ''),
    });

    const endpoints = ['/auth/updatepassword', '/admin/profile/password'];
    let lastError;

    for (const endpoint of endpoints) {
      try {
        return await adminApiRequest(endpoint, { method: 'PUT', body });
      } catch (err) {
        lastError = err;
        const { message, status } = parseThrownAdminError(err);
        const tryNext =
          status === 404 ||
          status === 405 ||
          /not found/i.test(message || '') ||
          isTeamRouteAuthorizationError(message);
        if (!tryNext) throw err;
      }
    }

    throw lastError || new Error(JSON.stringify({ error: 'Failed to change password' }));
  },

  // Admin: Update user password (no old password required)
  // Backend: PUT /api/admin/users/:userId/password { newPassword }
  updateUserPassword: async (userId, newPassword) => {
    const id = encodeURIComponent(String(userId ?? '').trim());
    if (!id) {
      return Promise.reject(new Error(JSON.stringify({ error: 'User id is required' })));
    }
    return adminApiRequest(`/admin/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword: String(newPassword ?? '') }),
    });
  },

  // Soft delete user
  deleteUser: async (userId, reason = 'Account closure requested') => {
    return adminApiRequest(`/admin/users/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason })
    });
  },

  // Restore deleted user
  restoreUser: async (userId) => {
    return adminApiRequest(`/admin/users/${userId}/restore`, {
      method: 'PUT'
    });
  },

  // Permanently delete user
  permanentDeleteUser: async (userId) => {
    return adminApiRequest(`/admin/users/${userId}/permanent`, {
      method: 'DELETE'
    });
  },

  // Get deleted users
  getDeletedUsers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const url = `/admin/users/deleted${queryString ? `?${queryString}` : ''}`;
    
    return adminApiRequest(url);
  },

  // Get users counts (combined - recommended)
  getUsersCounts: async () => {
    return adminApiRequest('/admin/users/counts');
  },

  // Get active users count
  getActiveUsersCount: async () => {
    return adminApiRequest('/admin/users/count/active');
  },

  // Get deleted users count
  getDeletedUsersCount: async () => {
    return adminApiRequest('/admin/users/count/deleted');
  },

  // Get total users count
  getTotalUsersCount: async () => {
    return adminApiRequest('/admin/users/count/total');
  },

  // Change user role
  changeUserRole: async (userId, role, reason = '') => {
    return adminApiRequest(`/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role, reason })
    });
  },

  // Activate/deactivate user
  changeUserStatus: async (userId, isActive, reason = '') => {
    return adminApiRequest(`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive, reason })
    });
  },

  // Get user statistics
  getUserStatistics: async () => {
    return adminApiRequest('/admin/users/statistics');
  },

  // Create new user
  createUser: async (userData) => {
    try {
      // Try admin endpoint first
      return await adminApiRequest('/admin/users', {
        method: 'POST',
        body: JSON.stringify(userData)
      });
    } catch (error) {
      console.log('🔄 Admin endpoint failed, trying regular registration endpoint...');
      
      // Fallback to regular registration endpoint
      try {
        const response = await fetch(getApiUrl('/auth/register'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken') || localStorage.getItem('authToken')}`
          },
          body: JSON.stringify(userData)
        });
        
        const data = await response.json();
        
        if (response.ok) {
          return { success: true, data: data.user || data, message: 'User created successfully via registration endpoint' };
        } else {
          throw new Error(JSON.stringify({ error: data.error || data.message || 'Registration failed' }));
        }
      } catch (fallbackError) {
        console.error('❌ Both admin and registration endpoints failed:', fallbackError);
        throw error; // Throw original error
      }
    }
  }
};

export default adminUserService;
